import { useMemo } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import TableHeader from "../../../../Features/Table/GenericDraggableTable/Renderers/TableHeader";
import Timestamp from "../../../../Features/Table/GenericDraggableTable/Renderers/columnTypes/Timestamp";
import { CanUpdate, CanDelete } from "../../../../Components/PermissionGuards";
import { FEATURE_CODES } from "../../../../Static/FeatureMapping";

const useLocationColumns = (locationLevels, onEdit, onDelete) => {
  return useMemo(() => {
    const cols = locationLevels.map((lvl) => ({
      title: (titleProps) => (
        <TableHeader
          titleProps={titleProps}
          config={{ title: lvl.type, dataIndex: lvl.level, sorter: true }}
        />
      ),
      dataIndex: lvl.level,
      key: lvl.level,
      minWidth: 160,
      sorter: (a, b) => (a[lvl.level] || "").localeCompare(b[lvl.level] || ""),
      sortDirections: ["ascend", "descend"],
      showSorterTooltip: false,
      render: (val) => (
        <Typography variant="fw400s14lh24c3D4B5A">{val || "—"}</Typography>
      ),
    }));

    cols.push({
      title: (titleProps) => (
        <TableHeader
          titleProps={titleProps}
          config={{ title: "Created At", dataIndex: "createdAt", sorter: true }}
        />
      ),
      dataIndex: "createdAt",
      key: "createdAt",
      width: 170,
      sorter: (a, b) => (a.createdAt || "").localeCompare(b.createdAt || ""),
      sortDirections: ["ascend", "descend"],
      showSorterTooltip: false,
      render: (_, record) => (
        <Timestamp
          config={{
            dataIndex: "createdAt",
            dateFormat: "DD MMM'YY hh:mm A",
            tooltipDateFormat: "relative",
            defaultValue: "—",
          }}
          record={record}
        />
      ),
    });

    cols.push({
      title: (titleProps) => (
        <TableHeader
          titleProps={titleProps}
          config={{ title: "Updated At", dataIndex: "updatedAt", sorter: true }}
        />
      ),
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 170,
      sorter: (a, b) => (a.updatedAt || "").localeCompare(b.updatedAt || ""),
      sortDirections: ["ascend", "descend"],
      showSorterTooltip: false,
      render: (_, record) => (
        <Timestamp
          config={{
            dataIndex: "updatedAt",
            dateFormat: "DD MMM'YY hh:mm A",
            tooltipDateFormat: "relative",
            defaultValue: "—",
          }}
          record={record}
        />
      ),
    });

    cols.push({
      title: (titleProps) => (
        <TableHeader titleProps={titleProps} config={{ title: "Actions" }} />
      ),
      key: "actions",
      width: 100,
      render: (_, row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CanUpdate feature={FEATURE_CODES.SUPPORTED_LOCATIONS}>
            <Tooltip title="Edit" componentsProps={{ tooltip: { sx: { fontSize: "10px" } } }}>
              <IconButton
                size="small"
                onClick={() => onEdit(row)}
                sx={{ color: "#6D72F6", padding: "6px", "&:hover": { backgroundColor: "#EEEFFF" } }}
              >
                <EditIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </CanUpdate>
          <CanDelete feature={FEATURE_CODES.SUPPORTED_LOCATIONS}>
            <Tooltip title="Delete" componentsProps={{ tooltip: { sx: { fontSize: "10px" } } }}>
              <IconButton
                size="small"
                onClick={() => onDelete(row)}
                sx={{ color: "#FF6364", padding: "6px", "&:hover": { backgroundColor: "#FFF0F0" } }}
              >
                <DeleteIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </CanDelete>
        </Box>
      ),
    });

    return cols;
  }, [locationLevels, onEdit, onDelete]);
};

export default useLocationColumns;
